import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import travelPricingAgeGroupObject from '@/entities/travelPricingAgeGroup/interface'

import {
  ALL_PERSONAL_ADDONS_QUERY,
  SINGLE_PERSONAL_ADDON_QUERY,
  PERSONAL_ADDONS_DEPENDENTS_QUERY,
  CREATE_PERSONAL_ADDON_MUTATION,
  UPDATE_PERSONAL_ADDON_MUTATION,
  DELETE_PERSONAL_ADDON_MUTATION,
  FORCE_DELETE_PERSONAL_ADDON_MUTATION,
  ADD_PERSONAL_ADDON_ALLOWED_AGE_GROUPS_MUTATION,
  REMOVE_PERSONAL_ADDON_ALLOWED_AGE_GROUPS_MUTATION,
  CREATE_PERSONAL_ADDON_ALLOWED_AGE_GROUPS_MUTATION,
  DELETE_PERSONAL_ADDON_ALLOWED_AGE_GROUPS_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioPersonalAddonRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_PERSONAL_ADDON_QUERY, variables).then((data: any) => {
      return hydrateObject(data.personalAddon)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_PERSONAL_ADDONS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.personalAddons)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_PERSONAL_ADDON_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.personalAddon),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_PERSONAL_ADDONS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.personalAddons),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(PERSONAL_ADDONS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([PERSONAL_ADDONS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_PERSONAL_ADDON_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.personalAddon)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_PERSONAL_ADDON_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.personalAddon)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_PERSONAL_ADDON_MUTATION, variables).then((data: any) => {
      return data.deleteRatioPersonalAddon
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_PERSONAL_ADDON_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioPersonalAddon
    })
  },

  assignAllowedAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        allowedAgeGroupIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PERSONAL_ADDON_ALLOWED_AGE_GROUPS_MUTATION, variables)
  },

  unassignAllowedAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        allowedAgeGroupIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PERSONAL_ADDON_ALLOWED_AGE_GROUPS_MUTATION, variables)
  },

  createAllowedAgeGroups: async (id: string, items: Array<travelPricingAgeGroupObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        allowedAgeGroups: items,
      },
    }
    return graphQLClient.request(CREATE_PERSONAL_ADDON_ALLOWED_AGE_GROUPS_MUTATION, variables)
  },

  deleteAllowedAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        allowedAgeGroupIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PERSONAL_ADDON_ALLOWED_AGE_GROUPS_MUTATION, variables)
  },
}

export default ratioPersonalAddonRepository
